import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../data/translations';
import { ArrowLeft, ArrowRight, Phone, Mail, MapPin, Check } from 'lucide-react';

const BASE = "https://www.cobras.be/sites/default/files/styles/card_medium/public/uploads/images/";

const contactCards = [
  {
    icon: Phone,
    title: { nl: "Bel ons", en: "Call us", fr: "Appelez-nous", de: "Rufen Sie uns an", es: "Llámenos" },
    image: BASE + "bishop-6_2.jpeg",
    content: [
      { label: "Jelle", value: "+32 (0)486 51 15 56", href: "tel:+32486511556" },
      { label: "Willem", value: "+32 (0)472 84 53 19", href: "tel:+32472845319" },
    ],
  },
  {
    icon: Mail,
    title: { nl: "Mail ons", en: "Email us", fr: "Envoyez-nous un mail", de: "Mailen Sie uns", es: "Envíenos un email" },
    image: BASE + "cobras_fabriek_17.jpg",
    content: [
      { label: "", value: "info@cobras.be", href: "mailto:info@cobras.be" },
    ],
  },
  {
    icon: MapPin,
    title: { nl: "Waar vind je ons?", en: "Where to find us?", fr: "Où nous trouver ?", de: "Wo finden Sie uns?", es: "¿Dónde encontrarnos?" },
    image: BASE + "bishop-3_0.jpeg",
    content: [
      { label: "", value: "Warande 93" },
      { label: "", value: "8501 Heule / België" },
    ],
    link: { label: { nl: "Routebeschrijving", en: "Get directions", fr: "Itinéraire", de: "Wegbeschreibung", es: "Cómo llegar" }, href: "https://www.google.com/maps/dir//Warande+93,+8501+Kortrijk" },
  },
];

const projectTypesMap = {
  nl:["Retail","Kantoor","Fabriek","Ontwikkeling","Privé"],
  en:["Retail","Office","Factory","Development","Private"],
  fr:["Commerce","Bureau","Usine","Développement","Privé"],
  de:["Einzelhandel","Büro","Fabrik","Entwicklung","Privat"],
  es:["Comercio","Oficina","Fábrica","Desarrollo","Privado"],
};

const ContactPage = () => {
  const { lang } = useLanguage();
  const { t, get } = useTranslation(lang);
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    location: '', deadline: '', projectType: '', budget: '', wishes: '',
    name: '', email: '', phone: '', company: '', privacyConsent: false,
  });

  useEffect(() => { window.scrollTo(0, 0); setTimeout(() => setLoaded(true), 100); }, []);

  const labels = {
    contact: { nl: "Contact", en: "Contact", fr: "Contact", de: "Kontakt", es: "Contacto" },
    projectInfo: { nl: "Projectinfo", en: "Project Info", fr: "Info projet", de: "Projektinfo", es: "Info del proyecto" },
    yourData: { nl: "Uw gegevens", en: "Your Details", fr: "Vos coordonnées", de: "Ihre Daten", es: "Sus datos" },
    location: { nl: "Wat is de locatie van het project?", en: "What is the project location?", fr: "Quel est l'emplacement du projet ?", de: "Wo befindet sich das Projekt?", es: "¿Cuál es la ubicación del proyecto?" },
    deadline: { nl: "Wanneer moet het project af zijn?", en: "When should the project be completed?", fr: "Quand le projet doit-il être terminé ?", de: "Wann soll das Projekt fertig sein?", es: "¿Cuándo debe completarse el proyecto?" },
    type: { nl: "Over welk type project gaat het?", en: "What type of project is it?", fr: "Quel type de projet ?", de: "Um welche Art von Projekt handelt es sich?", es: "¿Qué tipo de proyecto es?" },
    selectType: { nl: "- maak een keuze -", en: "- select -", fr: "- sélectionnez -", de: "- auswählen -", es: "- seleccionar -" },
    budgetLabel: { nl: "Wat is uw budget?", en: "What is your budget?", fr: "Quel est votre budget ?", de: "Was ist Ihr Budget?", es: "¿Cuál es su presupuesto?" },
    wishesLabel: { nl: "Wat zijn uw specifieke wensen?", en: "What are your specific wishes?", fr: "Quels sont vos souhaits spécifiques ?", de: "Was sind Ihre spezifischen Wünsche?", es: "¿Cuáles son sus deseos específicos?" },
    nextStep: { nl: "Volgende stap", en: "Next step", fr: "Étape suivante", de: "Nächster Schritt", es: "Siguiente paso" },
    prevStep: { nl: "Vorige stap", en: "Previous step", fr: "Étape précédente", de: "Vorheriger Schritt", es: "Paso anterior" },
    nameLabel: { nl: "Naam", en: "Name", fr: "Nom", de: "Name", es: "Nombre" },
    emailLabel: { nl: "Email", en: "Email", fr: "Email", de: "E-Mail", es: "Email" },
    phoneLabel: { nl: "Telefoon", en: "Phone", fr: "Téléphone", de: "Telefon", es: "Teléfono" },
    companyLabel: { nl: "Bedrijf", en: "Company", fr: "Entreprise", de: "Unternehmen", es: "Empresa" },
    send: { nl: "Verstuur", en: "Send", fr: "Envoyer", de: "Senden", es: "Enviar" },
    thankYou: { nl: "Bedankt! We nemen zo snel mogelijk contact met je op.", en: "Thank you! We will contact you as soon as possible.", fr: "Merci ! Nous vous contacterons dès que possible.", de: "Danke! Wir werden uns so schnell wie möglich bei Ihnen melden.", es: "¡Gracias! Nos pondremos en contacto lo antes posible." },
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const inputClass = "w-full bg-transparent border-b border-gray-600 focus:border-[#A855F7] text-white py-3 px-1 outline-none transition-colors text-base";

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      <div className="fixed inset-0 opacity-30 pointer-events-none z-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`, backgroundSize: '256px' }} />
      <div className="relative z-10">
        {/* Hero */}
        <div className="pt-28 md:pt-36 pb-12 px-8 md:px-16 lg:px-24">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-8 transition-colors"><ArrowLeft size={16} /> {get(t.common.home)}</button>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white uppercase tracking-tight italic" style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}>{get(labels.contact)}</h1>
        </div>

        {/* Contact Cards */}
        <div className="px-8 md:px-16 lg:px-24 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div key={i} className="group" style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(30px)', transition: `all 0.6s ease ${0.2 + i * 0.15}s` }}>
                  <div className="relative overflow-hidden rounded-sm aspect-[4/3] mb-5">
                    <img src={card.image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                    <Icon size={20} style={{ color: '#A855F7' }} /> {get(card.title)}
                  </h3>
                  {card.content.map((c, j) => (
                    <p key={j} className="text-gray-300 mb-1">
                      {c.label && <span className="font-medium text-white">{c.label} </span>}
                      {c.href ? <a href={c.href} className="hover:text-[#A855F7] transition-colors">{c.value}</a> : c.value}
                    </p>
                  ))}
                  {card.link && (
                    <a href={card.link.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-2 text-sm font-bold uppercase tracking-wider hover:gap-3 transition-all duration-300" style={{ color: '#A855F7' }}>
                      {get(card.link.label)} <ArrowRight size={14} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Section */}
        <div className="px-8 md:px-16 lg:px-24 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Left: Form */}
            <div>
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#A855F7' }}>
                    <Check size={32} className="text-white" />
                  </div>
                  <p className="text-xl text-white font-bold">{get(labels.thankYou)}</p>
                </div>
              ) : (
                <>
                  {/* Step indicators */}
                  <div className="flex items-center gap-4 mb-10">
                    <div className={`flex items-center gap-2 text-sm font-bold ${step >= 1 ? 'text-white' : 'text-gray-500'}`}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: step >= 1 ? '#A855F7' : '#333' }}>1</span>
                      {get(labels.projectInfo)}
                    </div>
                    <div className="flex-1 h-[1px] bg-gray-700" />
                    <div className={`flex items-center gap-2 text-sm font-bold ${step >= 2 ? 'text-white' : 'text-gray-500'}`}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: step >= 2 ? '#A855F7' : '#333' }}>2</span>
                      {get(labels.yourData)}
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {step === 1 && (
                      <div className="space-y-8">
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.location)}</label>
                          <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.deadline)}</label>
                          <input type="text" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.type)}</label>
                          <select value={form.projectType} onChange={e => setForm({ ...form, projectType: e.target.value })} className={inputClass + " bg-[#0a0a0a]"}>
                            <option value="">{get(labels.selectType)}</option>
                            {(projectTypesMap[lang] || projectTypesMap['nl']).map(pt => <option key={pt} value={pt}>{pt}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.budgetLabel)}</label>
                          <input type="text" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.wishesLabel)}</label>
                          <textarea value={form.wishes} onChange={e => setForm({ ...form, wishes: e.target.value })} rows={4} className={inputClass + " resize-none"} />
                        </div>
                        <button type="button" onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-8 py-3 text-sm font-bold tracking-[0.15em] uppercase text-white transition-all duration-500" style={{ backgroundColor: '#A855F7' }}>
                          {get(labels.nextStep)} <ArrowRight size={16} />
                        </button>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-8">
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.nameLabel)} *</label>
                          <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.emailLabel)} *</label>
                          <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.phoneLabel)}</label>
                          <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">{get(labels.companyLabel)}</label>
                          <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className={inputClass} />
                        </div>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="privacyConsent"
                            checked={form.privacyConsent}
                            onChange={e => setForm({ ...form, privacyConsent: e.target.checked })}
                            className="mt-1 w-4 h-4 accent-[#A855F7]"
                            required
                          />
                          <label htmlFor="privacyConsent" className="text-sm text-gray-400">
                            {get(t.contactGdpr.privacyConsent)}{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors" style={{color:'#A855F7'}}>
                              {get(t.contactGdpr.privacyPolicy)}
                            </a>.
                          </label>
                        </div>
                        <div className="flex gap-4">
                          <button type="button" onClick={() => setStep(1)} className="px-6 py-3 text-sm font-bold tracking-[0.15em] uppercase text-gray-300 border border-gray-600 hover:border-white hover:text-white transition-all duration-300">
                            {get(labels.prevStep)}
                          </button>
                          <button type="submit" disabled={!form.privacyConsent} className="inline-flex items-center gap-2 px-8 py-3 text-sm font-bold tracking-[0.15em] uppercase text-white transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed" style={{ backgroundColor: '#A855F7' }}>
                            {get(labels.send)} <ArrowRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </form>
                </>
              )}
            </div>

            {/* Right: Image */}
            <div className="hidden lg:block">
              <img src="https://www.cobras.be/sites/default/files/styles/lightbox/public/uploads/images/heule-defabriek-lr-001_0.jpg" alt="" className="w-full rounded-sm object-cover" style={{ maxHeight: '600px' }} />
            </div>
          </div>
        </div>

        {/* Legal note */}
        <div className="px-8 md:px-16 lg:px-24 pb-12">
          <p className="text-xs text-gray-600 max-w-2xl">
            {get(t.contactPage.legalNote)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
